/**
 * EPIC PLAT strings, Arabic (the source language).
 *
 * Copy is transcribed from `design/MedOSAdmin.dc.html` - the `platformTenants`,
 * `platformTenant` and `platformHealth` screens - and written in the same
 * register where the design leaves a gap (the impersonation dialog, the alert
 * buckets, the empty and error states).
 */
export const platformAr: Record<string, string> = {
  // --- Tenant list ---------------------------------------------------------
  'platform.tenants.title': 'العيادات المشتركة',
  'platform.tenants.subtitle':
    '{count} مستأجراً · {expiring} تجارب تنتهي هذا الأسبوع · لا تُعرض بيانات المرضى السريرية',
  'platform.tenants.filter.plan': 'الباقة',
  'platform.tenants.filter.status': 'الحالة',
  'platform.tenants.filter.all': 'الكل',
  'platform.tenants.filter.overLimit': 'تجاوزوا الحد فقط',
  'platform.tenants.search': 'ابحث باسم العيادة أو المعرّف',
  'platform.tenants.col.clinic': 'العيادة',
  'platform.tenants.col.plan': 'الباقة',
  'platform.tenants.col.status': 'الحالة',
  'platform.tenants.col.users': 'مستخدمون',
  'platform.tenants.col.patients': 'مرضى',
  'platform.tenants.col.storage': 'تخزين',
  'platform.tenants.col.lastActivity': 'آخر نشاط',
  'platform.tenants.empty': 'لا توجد عيادات مطابقة',
  'platform.tenants.emptyBody': 'جرّب توسيع البحث أو إزالة عوامل التصفية.',
  'platform.tenants.noActivity': 'لا نشاط',
  'platform.tenants.overLimit': 'تجاوز الحد',

  // --- KPI tiles -----------------------------------------------------------
  'platform.kpi.active': 'مستأجرون نشطون',
  'platform.kpi.trial': 'تجارب جارية',
  'platform.kpi.grace': 'فترة سماح',
  'platform.kpi.suspended': 'موقوفون',
  'platform.kpi.mrr': 'الإيراد الشهري المتكرر',

  // --- Tenant detail -------------------------------------------------------
  'platform.tenant.since': 'منذ {date}',
  'platform.tenant.changePlan': 'تغيير الباقة',
  'platform.tenant.requestSupport': 'طلب جلسة دعم',
  'platform.tenant.notice':
    'جلسة الدعم تتطلب غرضاً مكتوباً ومدة محددة وإعادة مصادقة، وتُظهر شريطاً دائماً داخل التطبيق، وتُدقَّق كل إجراء ينفَّذ خلالها. لا تُعرض تفاصيل المرضى السريرية إلا ضمن سياق دعم صريح.',
  'platform.tenant.usage.ACTIVE_USERS': 'مستخدمون نشطون',
  'platform.tenant.usage.ACTIVE_PATIENTS': 'مرضى نشطون',
  'platform.tenant.usage.BRANCHES': 'الفروع',
  'platform.tenant.usage.STORAGE_BYTES': 'التخزين',
  'platform.tenant.usage.MESSAGES_SENT': 'رسائل الشهر',
  'platform.tenant.usage.AI_OPERATIONS': 'عمليات الذكاء الاصطناعي',
  'platform.tenant.operations': 'مؤشرات التشغيل (غير سريرية)',
  'platform.tenant.ops.appointments': 'مواعيد آخر ٣٠ يوماً',
  'platform.tenant.ops.visits': 'زيارات مثبّتة',
  'platform.tenant.ops.invoices': 'فواتير مسوّاة',
  'platform.tenant.ops.patients': 'مرضى نشطون',
  'platform.tenant.ops.branches': 'الفروع النشطة',
  'platform.tenant.support': 'آخر إجراءات الدعم',
  'platform.tenant.support.empty': 'لا توجد إجراءات دعم مسجّلة',
  'platform.tenant.staff': 'فريق العيادة',
  'platform.tenant.staff.never': 'لم يسجّل الدخول بعد',
  'platform.tenant.notFound': 'لا توجد عيادة بهذا المعرّف',
  'platform.tenant.back': 'رجوع إلى القائمة',
  'platform.tenant.noSubscription': 'لا يوجد اشتراك مسجّل لهذه العيادة',
  'platform.tenant.unlimited': 'بلا حد',
  'platform.tenant.overrideReason': 'سبب آخر تعديل: {reason}',

  // --- Impersonation dialog ------------------------------------------------
  'platform.impersonate.title': 'طلب جلسة دعم',
  'platform.impersonate.tenant': 'العيادة',
  'platform.impersonate.purpose': 'غرض الجلسة',
  'platform.impersonate.purposeHint':
    'اذكر رقم البلاغ أو سبب الدخول. يُحفظ هذا النص في سجل التدقيق ويظهر للعيادة.',
  'platform.impersonate.purposePlaceholder': 'مثال: بلاغ فوترة رقم 4102 — الفاتورة لا تصدر',
  'platform.impersonate.duration': 'مدة الجلسة',
  'platform.impersonate.durationHint': 'تنتهي الجلسة تلقائياً على الخادم عند انقضاء المدة.',
  'platform.impersonate.minutes': '{count} دقيقة',
  'platform.impersonate.audited':
    'كل إجراء تنفّذه خلال هذه الجلسة يُسجَّل باسمك واسم العيادة. الوصول محدود بالقراءة التشغيلية فقط — لا تُعرض السجلات السريرية ولا الحركات المالية.',
  'platform.impersonate.submit': 'بدء الجلسة',
  'platform.impersonate.cancel': 'إلغاء',
  'platform.impersonate.needsPurpose': 'اكتب غرضاً واضحاً (٨ أحرف على الأقل)',
  'platform.impersonate.reauth':
    'هذا إجراء حساس. أعد تأكيد كلمة المرور من شاشة الحساب ثم أعد المحاولة.',
  'platform.impersonate.alreadyActive':
    'لديك جلسة دعم مفتوحة بالفعل. أنهِ الجلسة الحالية قبل بدء أخرى.',
  'platform.impersonate.started': 'بدأت الجلسة. أعد تحميل الصفحة للعمل داخل العيادة.',
  'platform.impersonate.active': 'جلسة مفتوحة',
  'platform.impersonate.endSession': 'إنهاء الجلسة',

  // --- Plan / status change ------------------------------------------------
  'platform.change.title': 'تغيير الباقة',
  'platform.change.plan': 'الباقة الجديدة',
  'platform.change.reason': 'سبب التغيير',
  'platform.change.reasonHint': 'إلزامي، ويُحفظ في سجل التدقيق ويظهر للعيادة.',
  'platform.change.submit': 'تطبيق التغيير',
  'platform.change.needsReason': 'اكتب سبباً واضحاً (٨ أحرف على الأقل)',

  // --- Service health ------------------------------------------------------
  'platform.health.title': 'صحة المنصة',
  'platform.health.subtitle': 'التبعيات الاختيارية معزولة — تعطّلها لا يوقف رعاية المرضى',
  'platform.health.required': 'أساسية',
  'platform.health.optional': 'اختيارية',
  'platform.health.state.up': 'تعمل',
  'platform.health.state.degraded': 'متدهورة',
  'platform.health.state.down': 'متوقفة',
  'platform.health.state.not_configured': 'غير مُهيّأة',
  'platform.health.jobs': 'المهام الخلفية',
  'platform.health.jobs.pending': 'قيد الانتظار',
  'platform.health.jobs.retrying': 'قيد إعادة المحاولة',
  'platform.health.jobs.dead': 'رسائل ميتة (DLQ)',
  'platform.health.jobs.lag': 'تأخر الطابور',
  'platform.health.alerts': 'تنبيهات التشغيل النشطة',
  'platform.health.alerts.empty': 'لا توجد تنبيهات تشغيل نشطة',
  'platform.health.checkedAt': 'آخر فحص: {time}',

  // --- Alerts --------------------------------------------------------------
  'platform.alerts.title': 'تنبيهات الاشتراكات',
  'platform.alerts.TRIAL_EXPIRING': 'تجربة تنتهي قريباً',
  'platform.alerts.TRIAL_EXPIRED': 'تجربة منتهية',
  'platform.alerts.GRACE': 'فترة سماح',
  'platform.alerts.SUSPENDED': 'موقوف',
  'platform.alerts.OVER_LIMIT': 'تجاوز حدود الباقة',
  'platform.alerts.empty': 'لا توجد تنبيهات — كل الاشتراكات ضمن حدودها',
  'platform.alerts.daysLeft': 'بقي {days} يوماً',
  'platform.alerts.daysAgo': 'منذ {days} يوماً',
  'platform.alerts.today': 'اليوم',

  // --- Subscription statuses ----------------------------------------------
  'platform.subStatus.TRIAL': 'تجريبي',
  'platform.subStatus.ACTIVE': 'نشط',
  'platform.subStatus.GRACE': 'فترة سماح',
  'platform.subStatus.SUSPENDED': 'موقوف',
  'platform.subStatus.CANCELLED': 'ملغى',
  'platform.subStatus.EXPIRED': 'منتهٍ',

  // --- Not-yet-built platform sub-screens ---------------------------------
  'platform.todo.title': 'هذه الشاشة ليست ضمن نطاق هذه المرحلة',
  'platform.todo.body':
    'إدارة إعدادات المنصة وتحليلات التفعيل وحزمة التشخيص وأوامر الإصلاح خارج نطاق PLAT الحالي. راجع وصف الطلب لما نُفِّذ وما لم يُنفَّذ.',
};
